<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable><k-card>
<k-card-title class="k-rcram-title"
	:style="`background-color:${report_banner_bgd_color}`"
>
	<img class="k-rcram-logo" 
		:style="report_logo_style"
		:src="report_logo"
	>
	<div class="k-rcram-report-title"
		v-html="report_title"
	></div>
	<k-spacer/>
	<k-btn v-if="!cram_job_id" color="primary" @click="$emit('dialog_cancel')">Done</k-btn>
</k-card-title>
<k-card-text class="k-rcram-body">
	<div class="k-rcram-h1">
		<div>Resource Set:  <b>{{resource_set_title}}</b></div>
		<k-spacer/>
		<div>{{comp_label_plural}}:  <a :href="comp_framework_href" target="_blank"><b>{{comp_framework_title}}</b></a></div>
	</div>
	<!-- ========================================================= -->
	<!-- -------------- GRID VIEW -------------------------------- -->
	<div class="k-rcram-table-outer elevation-5" v-if="initialized && dim=='grid'">
		<k-data-table light dense
			:headers="grid_headers"
			hide-default-header
			:items="grid_table_rows"
			:items-per-page="-1"
			hide-default-footer
			height="calc(100vh - 124px)"
			class="k-rcram-table"
		>
			<!-- HEADER ROWS -->
			<template v-slot:header><thead>
				<tr class="k-rcram-table-header">
					<th class="k-rcram-table-comp-header-cell" style="border-bottom-color:transparent!important;"></th>

					<th class="text-center py-3"
						style="font-size:18px!important;"
						:colspan="n_resources" 
					><b style="text-transform: uppercase;">{{resource_label_plural}}</b></th>
				</tr>

				<tr class="k-rcram-table-header">
					<th v-for="(header, i) in grid_headers"
						:key="(i==0) ? 'cell0' : header.resource_id"
						:id="(i==0) ? '' : `th-${header.resource_id}`"
						:width="(i==0) ? '340' : '100'"
						:class="(i==0) ? 
						       'k-rcram-table-comp-header-cell text-center'
							   : (grid_table_cell_class('', header.resource_id) + ' k-rcram-table-resource-header-cell text-center')
							   "
					>
						<div v-if="i==0" class="pb-2" style="font-size:18px!important;">
							<b style="text-transform:uppercase;">{{comp_label_plural}}</b>
							<!-- button to return to default sort by competencies -->
							<k-btn x-small color="#333" outlined class="ml-2"
								v-show="sort_by!='comps'"
								@click="sort_by='comps'"
							><k-icon small color="#333" class="mr-2">fas fa-sort</k-icon>Default Sort</k-btn>
						</div>

						<!-- Resource header cells -->
						<div v-else class="k-rcram-resource-header-div">
							<div v-html="header.text"></div>
							<div class="k-rcram-resource-header-btns">

								<!-- button to sort by this resource -->
								<k-tooltip bottom><template v-slot:activator="{on}">
									<k-btn v-on="on" x-small color="#333" outlined
										@click="sort_by={dim:'resource', id:header.resource_id}"
									><k-icon small color="#333">fas fa-sort</k-icon></k-btn>
								</template>Sort by this {{resource_label}}</k-tooltip>

								<!-- button to show details for this resource -->
								<k-tooltip bottom><template v-slot:activator="{on}">
									<k-btn v-on="on" x-small color="#333" outlined class="ml-1"
										@click="view_resource(header.resource_id)"
									><k-icon small color="#333">fas fa-rectangle-list</k-icon></k-btn>
								</template>List View for this {{resource_label}}</k-tooltip>

							</div>
						</div>
					</th>
				</tr>
			</thead></template>

			<!-- BODY ROWS -->
	 		<template v-slot:item="{ item }"><tr>
				<!-- Competency header cells -->
				<td class="k-rcram-table-comp-header-cell" width="340"
					:class="grid_table_cell_class(item.comp_id, '')"
					:id="`th-${item.comp_id}`"
				>
					<div v-html="item.comp_text"></div>
					<div class="k-rcram-comp-header-btns">

						<!-- button to sort by this comp -->
						<k-tooltip bottom><template v-slot:activator="{on}">
							<k-btn v-on="on" x-small color="#333" outlined
								@click="sort_by={dim:'comp', id:item.comp_id}"
							><k-icon small color="#333">fas fa-fw fa-sort fa-rotate-90</k-icon></k-btn>
						</template>Sort by this {{comp_label}}</k-tooltip>

						<!-- button to show details for this comp -->
						<k-tooltip bottom><template v-slot:activator="{on}">
							<k-btn v-on="on" x-small color="#333" outlined class="mt-1"
								@click="view_comp(item.comp_id)"
							><k-icon small color="#333">fas fa-fw fa-rectangle-list</k-icon></k-btn>
						</template>List View for this {{comp_label}}</k-tooltip>

					</div>
				</td>

				<!-- simscore cells for each resource-comp pair -->
				<td v-for="(cell) in item.cells"
					:key="cell.resource_id"
					class="k-rcram-table-simscore-cell"
					:class="`${grid_table_cell_class(item.comp_id, cell.resource_id)} ${cell.cat.cell_class}`"
				>
					<k-icon>{{cell.cat.icon}}</k-icon>
				</td>
			</tr></template>

		</k-data-table>
	</div>
	
	<!-- ========================================================= -->
	<!-- -------------- COMPETENCY LIST -------------------------- -->
	<div class="k-rcram-table-outer pa-3 elevation-5" v-if="initialized && dim=='comp'">
		<div class="mb-3 text-center">
			<k-btn small color="primary"
				@click="view_grid"
			><k-icon small class="mr-2">fas fa-table-cells</k-icon>Show Grid View</k-btn>
		</div>

		<div class="mb-2 d-flex align-start k-rcram-list-header">
			<div class="mr-3"><b>{{comp_label}}:</b></div>
			<div v-html="cfitem_title(comp_ditem)"></div>
		</div>

		<div class="mb-3 d-flex align-center">
			<div class="mr-10"><b>Top Alignment Suggestions</b></div>
			<div class="mr-2">Show:</div>
			<div style="width:200px">
				<k-select dense outlined hide-details="auto" 
					:items="resource_level_options" 
					v-model="resource_level_to_show_local" 
				></k-select>
			</div>
		</div>

		<!-- List item for each top-level resource (i.e. Chapter) -->
		<div class="k-cram-list-item-outer"
			v-for="(row, i) in comp_list_rows" :key="row.id"
			v-if="i < list_n_showing"
		>

			<div class="k-cram-list-item-header d-flex align-center"
				:class="row.cat.cell_class"
			>
				<div class="k-cram-list-cat-icon">
					<k-tooltip bottom><template v-slot:activator="{on}">
						<k-icon v-on="on">{{row.cat.icon}}</k-icon>
					</template>Similarity Score: <b>{{row.simscore}}</b></k-tooltip>
				</div>
				<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{row.simscore}}</div>
				<div class="k-cram-list-item-type">{{row.cfi.CFItemType}}</div>
				<div v-html="cfitem_title(row.cfi)"></div>

				<k-btn x-small text color="primary" class="ml-2 k-tight-btn" style="margin-top:3px;"
					v-if="row.cfi.extensions && row.cfi.extensions.description"
					@click="toggle_description(row.id)"
				>
					{{descriptions_showing[row.id]?'Hide':'Show'}} {{description_label}}
					<k-icon small class="ml-1" style="margin-top:-6px;">fas fa-fw fa-sort-down</k-icon>
				</k-btn>
				
				<k-btn x-small text color="primary" class="ml-1"
					v-if="resource_level_to_show=='l0'"
					@click="view_resource(row.id)"
				><k-icon small>fas fa-fw fa-rectangle-list</k-icon></k-btn>

				<k-spacer/>
			</div>

			<div class="k-cram-resource-description ml-10 mt-1 mb-2"
				v-if="descriptions_showing[row.id]" 
				v-html="U.marked_latex(row.cfi.extensions.description)"
			></div>


			<!-- Controls for sub-resources -->
			<div v-if="resource_level_to_show=='l0'" class="my-2 ml-3 d-flex align-center">
				<k-btn outlined small color="primary" class="k-tight-btn mr-4"
					v-if="row.subrows.length > 0"
					@click="toggle_components_for(row.id)"
				>{{components_showing[row.id] ? 'Hide' : 'Show'}} {{sub_resource_label_plural}}</k-btn>

				<div v-if="components_showing[row.id]" class="d-flex align-center">
					<div class="mr-2">Sort by:</div>
					<div style="width:200px">
						<k-select dense outlined hide-details="auto" 
							:items="list_view_sort_by_options" 
							v-model="resource_subrow_sort_by" 
						></k-select>
					</div>
				</div>

			</div>

			<!-- List items for each SUB-RESOURCE (i.e. Section) -->
			<div v-if="resource_level_to_show=='l0' && components_showing[row.id]">
				<div v-for="(subrow, i) in row.subrows" :key="subrow.id" class="my-2 mx-8">
					<div class="k-cram-list-item-header d-flex align-center"
						:class="subrow.cat.cell_class"
					>
						<div class="k-cram-list-cat-icon">
							<k-tooltip bottom><template v-slot:activator="{on}">
								<k-icon v-on="on">{{subrow.cat.icon}}</k-icon>
							</template>Similarity Score: <b>{{subrow.simscore}}</b></k-tooltip>
						</div>
						<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{subrow.simscore}}</div>
						<div class="k-cram-list-item-type">{{subrow.cfi.CFItemType}}</div>
						<div v-html="cfitem_title(subrow.cfi)"></div>

						<k-btn x-small text color="primary" class="ml-2 k-tight-btn" style="margin-top:3px;"
							@click="toggle_description(subrow.id)"
						>
							{{descriptions_showing[subrow.id]?'Hide':'Show'}} {{description_label}}
							<k-icon small class="ml-1" style="margin-top:-6px;">fas fa-fw fa-sort-down</k-icon>
						</k-btn>

						<k-spacer/>
					</div>

					<div class="k-cram-resource-description ml-10 mt-1 mb-4"
						v-if="descriptions_showing[subrow.id]" 
						v-html="U.marked_latex(subrow.cfi.extensions.description)"
					></div>
				</div>
			</div>
		</div>

		<div class="text-center"
			v-if="list_show_show_more_btn"
		>
			<k-btn small color="#ccc"
				@click="show_more"
			>Show More (+{{comp_list_rows.length - list_n_showing}})</k-btn>
		</div>
	</div>

	<!-- ========================================================= -->
	<!-- -------------- RESOURCES LIST --------------------------- -->
	<div class="k-rcram-table-outer pa-3 elevation-5" v-if="initialized && dim=='resource'">
		<div class="mb-3 text-center">
			<k-btn small color="primary"
				@click="view_grid"
			><k-icon small class="mr-2">fas fa-table-cells</k-icon>Return to Grid View</k-btn>
		</div>

		<div class="mb-2 d-flex align-start k-rcram-list-header">
			<div class="mr-3"><b>{{resource_label}}:</b></div>
			<div v-html="cfitem_title(resource_ditem)"></div>
		</div>

		<div class="mb-3 d-flex align-center">
			<div class="mr-10"><b>Top Alignment Suggestions</b></div>
		</div>

		<!-- List item for each top-level competency -->
		<div class="k-cram-list-item-outer"
			v-for="(row, i) in resource_list_rows" :key="row.id"
			v-if="i < list_n_showing"
		>

			<div class="k-cram-list-item-header d-flex align-center"
				:class="row.cat.cell_class"
			>
				<div class="k-cram-list-cat-icon">
					<k-tooltip bottom><template v-slot:activator="{on}">
						<k-icon v-on="on">{{row.cat.icon}}</k-icon>
					</template>Similarity Score: <b>{{row.simscore}}</b></k-tooltip>
				</div>
				<div v-if="simscores_showing" class="k-cram-list-item-simscore">{{row.simscore}}</div>
				<div class="k-cram-list-item-type">{{row.cfi.CFItemType}}</div>
				<div v-html="cfitem_title(row.cfi)"></div>

				<k-btn x-small text color="primary" class="ml-1"
					@click="view_comp(row.id)"
				><k-icon small>fas fa-fw fa-rectangle-list</k-icon></k-btn>

				<k-spacer/>
			</div>
		</div>

		<div class="text-center"
			v-if="list_show_show_more_btn"
		>
			<k-btn small color="#ccc"
				@click="show_more"
			>Show More (+{{resource_list_rows.length - list_n_showing}})</k-btn>
		</div>
	</div>

	<!-- ========================================================= -->
	<!-- -------------- KEY -------------------------------------- -->
	<div v-if="key_showing" class="k-rcram-key elevation-3">
		<div class="text-center mb-1">
			<k-btn small outlined color="primary"
				@click="key_showing = false"
			><k-icon small class="mr-2">fas fa-key</k-icon> Hide Key</k-btn>
		</div>

		<div v-for="(cat, i) in recommendation_categories" class="k-rcram-key-entry"
			:class="cat.cell_class"
		>
			<span style="display:inline-block; width:30px; text-align:center">
				<k-icon small>{{cat.icon}}</k-icon>
			</span>
			{{cat.label}}
		</div>

		<div style="position:absolute; right:0; top:0; width:16px; height:16px; cursor:pointer" @click="simscores_showing=!simscores_showing"></div>
	</div>

	<div v-if="!key_showing" class="k-rcram-key-btn">
		<k-btn small color="primary"
			@click="key_showing = true"
		><k-icon small class="mr-2">fas fa-key</k-icon> Show Key</k-btn>
	</div>

</k-card-text>
</k-card></k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import SCRAMReportMixin from './SCRAMReportMixin.js'

export default {
	mixins: [SCRAMReportMixin,],
	props: {
		// req: { type: String, required: true },
		cram_params: { required: false, default() { return null } },
		cram_job_id: { type: String, required: false, default() { return '1001'} },
		dim: { type: String, required: false, default() { return 'grid'} },
		dim_id: { type: String, required: false, default() { return ''} },
		resource_level_to_show: { type: String, required: false, default() { return 'l0'} },
	},
	data() { return {
		dialog_open: true,

		sort_by: 'comps',
		n_showing: 0,
		components_showing: {},
		table_row_update: true,
		key_showing: true,
		simscores_showing: true,
		descriptions_showing: {},

		SHOW_INITIAL_BLOCK_SIZE: 4,
		SHOW_MORE_BLOCK_SIZE: 25,

		resource_subrow_sort_by: 'simscore',
		list_view_sort_by_options: [
			{ value: 'sequence', text: 'Order in Chapter' },
			{ value: 'simscore', text: 'Similarity Score' },
		],

		resource_level_to_show_local: 'l0',

		// NOTE: we're currently using the scram service thresholds for determining these categories in this report
		recommendation_categories: [
			{
				index: 0,
				label: 'Highly Likely Direct Alignment',
				threshold: 80,
				cell_class: 'k-rcram-table-simscore-cell-4',
				icon: 'fas fa-fw fa-circle-star'
			},
			{
				index: 1,
				label: 'Likely Direct Alignment',
				threshold: 50,
				cell_class: 'k-rcram-table-simscore-cell-4',
				icon: 'fas fa-fw fa-circle'
			},
			{
				index: 2,
				label: 'Likely Partial Support',
				threshold: 35,
				cell_class: 'k-rcram-table-simscore-cell-3',
				icon: 'fas fa-fw fa-circle-half-stroke'
			},
			{
				index: 3,
				label: 'Possible Contextual Relevance',
				threshold: 20,
				cell_class: 'k-rcram-table-simscore-cell-2',
				icon: 'far fa-fw fa-circle'
			},
			{
				index: 4,
				label: 'Minimal/Surface Relevance',
				threshold: 10,
				cell_class: 'k-rcram-table-simscore-cell-1',
				icon: 'far fa-fw fa-circle'
			},
			{
				index: 5,
				label: 'Not Relevant',
				threshold: 0,
				cell_class: 'k-rcram-table-simscore-cell-0',
				icon: 'fas fa-fw fa-minus'
			},
		],
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),

		initialized() {
			return this.matrix && this.rfr && this.cfr
		},

		resource_ids() {
			if (!this.initialized) return []

			let matrix = this.matrix
			// if we're showing a list for a competency and resource_level_to_show is 1, use sub_matrix
			if (this.dim == 'comp' && this.resource_level_to_show == 'l1') {
				matrix = this.sub_matrix
			}

			let arr = []
			for (let comp_id in matrix) {
				for (let resource_id in matrix[comp_id]) {
					arr.push(resource_id)
				}
				break
			}

			// if we're sorting by a competency, do so by sorting the resource_ids here
			if (this.sort_by?.dim == 'comp') {
				// console.warn('update resource_ids')
				this.table_row_update = false
				setTimeout(x=>this.table_row_update = true, 0)

				let comp_id = this.sort_by.id
				arr.sort((a,b)=>{
					return matrix[comp_id][b].simscore - matrix[comp_id][a].simscore
				})
			}

			return arr
		},
		n_resources() { return this.resource_ids.length },

		comp_ids() {
			if (!this.initialized) return []

			let arr = []
			for (let comp_id in this.matrix) {
				arr.push(comp_id)
			}

			// if we're sorting by a resource, do so by sorting the comp_ids here
			if (this.sort_by?.dim == 'resource') {
				// console.warn('update comp_ids')
				this.table_row_update = false
				setTimeout(x=>this.table_row_update = true, 0)

				let resource_id = this.sort_by.id
				arr.sort((a,b)=>{
					return this.matrix[b][resource_id].simscore - this.matrix[a][resource_id].simscore
				})
			}

			return arr
		},
		n_comps() { return this.comp_ids.length },

		grid_headers() {
			// headers (columns) are resources (e.g. chapters)
			let arr = []

			// first column is the competencies (e.g. standards)
			arr.push({
				text: 'COMPETENCY',
				align: 'left',
				// sortable: false,
				value: 'comp_text',
			})

			// one column per resource
			for (let resource_id of this.resource_ids) {
				let r = this.resource_cfitems[resource_id]
				arr.push({
					text: this.cfitem_title(r, 60),
					resource_id: resource_id,
					align: 'center',
					value: `simscore-${resource_id}`,
					cfi: r,
				})
			}
			return arr
		},

		grid_table_rows() {
			if (!this.initialized) return []
			if (!this.table_row_update) return []

			// console.warn('update grid_table_rows: ' + this.table_row_update)
			let arr = []

			// one row per comp
			let i = 0
			for (let comp_id of this.comp_ids) {
				let c_cfi = this.comp_cfitems[comp_id]
				++i

				// first cell is the comp itself; get that info
				let o = {
					comp_id: comp_id,
					comp_sequence: i,
					comp_text: this.cfitem_title(c_cfi, 120),
					c_cfi: c_cfi,
					
					// this will hold the cells
					cells: []
				}

				for (let resource_id of this.resource_ids) {
					let cell = this.matrix[comp_id][resource_id]
					let r_cfi = this.resource_cfitems[resource_id]
					o[`simscore-${resource_id}`] = cell.simscore
					o.cells.push({
						r_cfi: r_cfi,
						resource_id: resource_id,
						resource_text: this.cfitem_title(r_cfi),
						cell: cell,
						simscore: cell.simscore,
						cat: this.rco(cell)
					})
				}

				arr.push(o)
			}

			return arr
		},

		// display item when dim == 'comp'
		comp_ditem() {
			return this.comp_cfitems[this.dim_id]
		},

		// display item when dim == 'resource'
		resource_ditem() {
			return this.resource_cfitems[this.dim_id]
		},

		comp_list_rows() {
			let arr = []

			let matrix = this.resource_level_to_show == 'l0' ? this.matrix : this.sub_matrix

			// list of resources for a given competency
			for (let resource_id of this.resource_ids) {
				let cell = matrix[this.dim_id][resource_id]
				let cfi = this.resource_cfitems[resource_id]

				let o = {
					cfi: cfi,
					id: resource_id,
					cell: cell,
					simscore: cell.simscore,
					cat: this.rco(cell),
				}

				if (this.resource_level_to_show == 'l0') {
					// get "subrows" for this resource
					let subrows = []
					let children = cfi.tree_nodes[0]?.children
					if (children) {
						for (let child of children) {
							let sub_cell = this.sub_matrix[this.dim_id][child.cfitem.identifier]
							if (empty(sub_cell)) {
								console.warn(`couldn’t find sub_cell for ${this.dim_id} / ${child.cfitem.identifier}`)
							}
							subrows.push({
								cfi: child.cfitem,
								id: child.cfitem.identifier,
								cell: sub_cell,
								simscore: sub_cell.simscore,
								cat: this.rco(sub_cell)
							})
						}

						// IFF resource_subrow_sort_by is 'simscore', sort subrows by simscore descending (otherwise we want to sort in child order, which is what we'll have if we don't sort)
						if (this.resource_subrow_sort_by == 'simscore') {
							subrows.sort((a,b) => b.simscore - a.simscore)
						}
					}
					o.subrows = subrows
				}

				arr.push(o)
			}

			return arr
		},

		resource_list_rows() {
			let arr = []

			// list of competencies for a given resource
			for (let comp_id of this.comp_ids) {
				let cell = this.matrix[comp_id][this.dim_id]
				let cfi = this.comp_cfitems[comp_id]
				arr.push({
					cfi: cfi,
					id: comp_id,
					cell: cell,
					simscore: cell.simscore,
					cat: this.rco(cell),
				})
			}

			return arr
		},

		list_default_n_showing() {
			let n = 0
			let current_cat_index
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			for (let row of arr) {
				// always show at least SHOW_INITIAL_BLOCK_SIZE items
				if (n < this.SHOW_INITIAL_BLOCK_SIZE) { ++n; continue; }

				// always show likely alignments
				if (row.cat.index <= 1) { ++n; continue; }

				// if we get to here and current_cat_index isn't set, set to the this row's index
				if (empty(current_cat_index)) {
					current_cat_index = arr[n-1].cat.index
				}
				
				// show all items with current_cat_index, until we get to 0
				if (current_cat_index < 5 && row.cat.index == current_cat_index) { ++n; continue; }

				// break when we get to here
				break
			}
			return n
		},

		list_n_showing() {
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			if (this.n_showing == -1) {
				return this.arr.length

			} else if (this.n_showing == 0) {
				return this.list_default_n_showing

			} else {
				return this.n_showing
			}
		},

		list_show_show_more_btn() {
			let arr = this.dim == 'comp' ? this.comp_list_rows : this.resource_list_rows
			if (this.n_showing >= arr.length) return false
			return true
		},

		resource_level_options() {
			return [
				// TODO: make this a computed and use item types
				{ value: 'l0', text: this.resource_label_plural },
				{ value: 'l1', text: this.sub_resource_label_plural },
			]
		},

		report_title() {
			// TODO: store this as part of the job
			return `<b>BJU Press</b> Educational Standards Correlation and Comparison Report: Grid View`
			// return `<b>Satchel Rosetta Align</b> Standards Alignment Report: Grid View`
		},
	},

//////////////////////////////////////////
	watch: {
		// any time dim_id changes, dim must also change; and both seem to always be changed at the same time
		// dim: { immediate: true, handler: 'handle_prop_change' },
		dim_id: { immediate: true, handler: 'handle_prop_change' },
		sort_by: { handler: 'handle_sort_by' },

		resource_level_to_show() {
			this.resource_level_to_show_local = this.resource_level_to_show
		},

		resource_level_to_show_local() {
			if (this.dim == 'comp') {
				this.view_comp(this.dim_id)
			}
		},
	},

/////////////////////////////////////////
	created() {
		vapp.resource_matrix_view = this
		this.resource_level_to_show_local = this.resource_level_to_show
		this.load_data()
	},
	mounted() {
	},

/////////////////////////////////////////
	methods: {
		handle_prop_change() {
			// console.warn(`dim: ${this.dim} / dim_id: ${this.dim_id}`)
			if (this.dim == 'grid') {
				// leave sort at whatever we were last sorted by
				// and call the sort handler
				setTimeout(x=>this.handle_sort_by(), 0)

			} else {
				// sort by the id
				this.sort_by = { dim: this.dim, id: this.dim_id }
			}
			this.n_showing = 0
		},

		handle_sort_by() {
			// console.warn('handle_sort_by top: ' + this.dim)
			// if we're showing the grid view, scroll when sort_by is changed
			if (this.dim != 'grid') return

			U.loading_start()
			let container = '.k-rcram-table-outer .v-data-table__wrapper'
			if ($(container).length == 0) return	// container is not yet rendered??

			// default sort: go to the top-left
			if (this.sort_by == 'comps') {
				setTimeout(x=>{
					$(container)[0].scrollTop = 0
					$(container)[0].scrollLeft = 0
					U.loading_stop()
				}, 50)

			// if we're sorting by a resource or comp, scroll to it after a delay
			} else {
				let selector = `#th-${this.sort_by.id}`	// this works regardless of `dim` value
				
				// sort by comp (row): scroll to the left, then scroll vertically to the competency row
				if (this.sort_by.dim == 'comp') {
					setTimeout(x=>{
						$(container)[0].scrollLeft = 0
						// use goto to scroll horizontally to the comp's row
						this.$vuetify.goTo(selector, {
							container: container, 
							duration:300, 
							offset:60,
						})
						U.loading_stop()
					}, 50)

				// sort by resource (column): scroll to the top, then scroll horizontally to the resource column
				} else {
					setTimeout(x=>{
						$(container)[0].scrollTop = 0
						// offset must take account of the competency cell on the left
						U.scroll_horizontal_to(selector, container, 340)
						U.loading_stop()
					}, 50)
				}
			}
		},

		// not currently used; we let the server navigate the tree
		// get_descendent_ids(parent, item_type, arr) {
		// 	for (let child of parent.children) {
		// 		if (child.cfitem.CFItemType == item_type) {
		// 			arr.push(child.cfitem.identifier)
		// 		}
		// 		if (child.children.length > 0) {
		// 			this.get_descendent_ids(child, item_type, arr)
		// 		}
		// 	}
		// },
		// 	this.get_descendent_ids(resource_parent_node, this.resource_item_type, resource_ids)

		rco(cell) {
			// recommendation_categories[0] corresponds to alignment_category 6
			// recommendation_categories[5] corresponds to alignment_category 1
			return this.recommendation_categories[this.recommendation_categories.length - cell.alignment_category]
		},

		grid_table_cell_class(comp_id, resource_id) {
			if (this.sort_by?.dim == 'resource') {
				if (resource_id == this.sort_by.id) {
					return 'k-rcram-sorted-resource-cell'
				}
				
			} else if (this.sort_by?.dim == 'comp') {
				if (comp_id == this.sort_by.id) {
					return 'k-rcram-sorted-comp-cell'
				}
			}
		},

		view_grid() {
			this.$router.push({ path: `/crm/${this.cram_job_id}/grid/all/${this.resource_level_to_show_local}` })
			this.n_showing = 0
		},

		view_comp(id) {
			// sort by the comp
			this.sort_by = { dim: 'comp', id: id }
			this.$router.push({ path: `/crm/${this.cram_job_id}/comp/${id}/${this.resource_level_to_show_local}` })
			this.n_showing = 0
		},

		view_resource(id) {
			// sort by the comp
			this.sort_by = { dim: 'resource', id: id }
			this.$router.push({ path: `/crm/${this.cram_job_id}/resource/${id}/${this.resource_level_to_show_local}` })
			this.n_showing = 0
		},

		show_more() {
			this.n_showing += this.SHOW_MORE_BLOCK_SIZE
		},

		toggle_components_for(id, val) {
			let o = structuredClone(this.components_showing)
			if (empty(val)) val = !o[id]
			o[id] = val
			this.components_showing = o
		},

		toggle_description(id, val) {
			let o = structuredClone(this.descriptions_showing)
			if (empty(val)) val = !o[id]
			o[id] = val
			this.descriptions_showing = o
		},
			
		// this fn generates SQL insert statements for the BJU demo data
		// async export_table_inserts() {
		// 	let sub_payload = {
		// 		filename: 'section-matrix-bju-va'
		// 	}
		// 	let sub_result = await U.ajax_promise('get_last_alignment_matrix', sub_payload)
		// 	if (empty(sub_result?.json)) {
		// 		console.error('couldn’t fetch sub_matrix')
		// 		return
		// 	}

		// 	// remove comments
		// 	let json = sub_result.json.replace(/^(?:[ \t]*\/\/[^\n]*\n)+/, '')
		// 	json = JSON.parse(json)

		// 	let s = ''
		// 	for (let competency_id in json) {
		// 		for (let resource_id in json[competency_id]) {
		// 			let o = json[competency_id][resource_id]
		// 			// INSERT INTO sparklsalt.ai_resource_alignment_data (resource_id, competency_id, sbcosim, genaiscore) values (UUID_TO_BIN('076660f1-cec1-48f6-ab34-27dbf568906f'), UUID_TO_BIN('5ff14cb1-f84b-4387-9f44-e8395aa1e413'), 294, 1)
		// 			s += `INSERT INTO sparklsalt.ai_resource_alignment_data (resource_id, competency_id, sbcosim, genaiscore) values (UUID_TO_BIN('${resource_id}'), UUID_TO_BIN('${competency_id}'), ${o.cosim}, ${o.ai_eval});\n`
		// 		}
		// 	}

		// 	console.log(s)
		// },
	}
}
</script>

<style lang="scss">
.k-rcram-title {
	border-bottom:1px solid transparent;
	background-color:#eee;
	padding:0 24px 0 70px!important;
	height:60px;
}

.k-rcram-report-title {
	margin-left:8px;
}

.k-rcram-logo {
	position:fixed;
	left:0px;
	top:0px;
	height:60px;
}

.k-rcram-body {
	padding:12px!important;
	font-size:16px;
}

.k-rcram-h1 {
	display:flex;
	font-size:20px;
	line-height:24px;
	margin:0 4px 6px 4px;
}
.k-rcram-h2 {
	font-size:18px;
	line-height:21px;
	margin-bottom:12px;
}

.k-rcram-table-outer {
	overflow:scroll;
	border-radius:8px;
	border:1px solid #000;

	// this makes rendering marked latex work better
	p { margin:0!important; }
}

.k-rcram-table {
	max-width:none!important;

	.v-data-table__wrapper {
		// this makes it so that we can always scroll the resource columns to right next to the competencies
		padding-right:calc(100vw - 372px);
	}

	table {
		// without this the table is squeezed to 100%
		width:max-content!important;
		table-layout: fixed;
		border-top:1px solid #ccc;
		border-right:1px solid #000!important;
	}
}

/* Make all header cells sticky at the top */
.k-rcram-table th {
	position: sticky;
	top: 0;
	z-index: 2;
	background-color: var(--v-background-base, #fff);
}

/* Pin the first column body cells to the left */
.k-rcram-table td:first-child {
	position: sticky;
	left: 0;
	z-index: 3;
	background-color: var(--v-background-base, #fff);
}

/* Pin top-left intersection cell (First Column Header) */
.k-rcram-table th:first-child {
	position: sticky;
	top: 0;
	left: 0;
	z-index: 4;
	background-color: var(--v-background-base, #fff);
}

.k-rcram-table-header {
	th {
		padding: 4px 6px !important;
		height: 12px !important;
		font-size: 12px !important;
		line-height:14px!important;
		vertical-align: bottom;
		font-weight: normal;
		cursor: pointer;
		color:#000!important;
		border-left:1px solid #ccc;
		vertical-align:top;
		// background-color:#ddd;
	}
}

.k-rcram-table-comp-header-cell {
	// width:300px!important;
	font-size:12px!important;
	line-height:15px;
	// background-color:#ddd;
	padding:6px 40px 6px 6px!important;
	border-left:1px solid #ccc;
	border-right:1px solid #ccc;
	position:relative;
	border-right:1px solid #000!important;

	// this makes rendering marked latex work better
	p { margin:0!important; }

	.v-btn { opacity:0.3; }
}
.k-rcram-table-comp-header-cell:hover {
	.v-btn { opacity:1; }
}

th.k-rcram-table-comp-header-cell {
	vertical-align:bottom!important;
	border-bottom:1px solid #000!important;
}

.k-rcram-comp-header-btns {
	position:absolute;
	right:0px;
	top:0;
	display:flex;
	flex-direction:column;
	justify-content: center;
	align-items: center;
	padding-right:4px;
	height:100%;

	.v-btn {
		min-width:32px!important;
		padding:0!important;
	}
}

.k-rcram-table-resource-header-cell {
	// width:100px;
	font-size:12px!important;
	line-height:15px;
	border-left:1px solid #ccc;
	border-top:1px solid #ccc;
	border-bottom:1px solid #000!important;
	text-align:center;
	position:relative;
	.v-btn { opacity:0.3; }
}
.k-rcram-table-resource-header-cell:hover {
	.v-btn { opacity:1; }
}

.k-rcram-table-resource-header-cell.k-rcram-sorted-resource-cell {
	border-top:2px solid #000!important;
}

.k-rcram-resource-header-div {
	padding-bottom:26px!important;
}

.k-rcram-resource-header-btns {
	position:absolute;
	bottom:0;
	left:0;
	width:100%;
	display:flex;
	justify-content: center;
	padding-bottom:6px;
	.v-btn {
		min-width:32px!important;
		padding:0!important;
	}
}

.k-rcram-table-simscore-cell {
	// width:100px;
	font-size:12px!important;
	line-height:15px;
	border-left:1px solid #ccc;
	text-align:center;
}

// it's imported that the `-sorted-` selectors appear above the `-simscore-` selectors so that the background-colors cascade poperly
.k-rcram-sorted-resource-cell {
	border-left:2px solid #000!important;
	border-right:2px solid #000!important;
	font-weight:bold!important;
	background-color: #ccc!important;
	.v-btn { opacity:1; }
}
.k-rcram-sorted-comp-cell {
	border-top:2px solid #000!important;
	border-bottom:2px solid #000!important;
	font-weight:bold!important;
	background-color: #ccc!important;
	.v-btn { opacity:1; }
}

.k-rcram-table-simscore-cell-4 {   
	background-color:$v-green-lighten-3!important;
	.v-icon { color: $v-green-darken-4; }
}
.k-rcram-table-simscore-cell-3 {   
	background-color:$v-blue-lighten-3!important;
	.v-icon { color: $v-blue-darken-4; }
}
.k-rcram-table-simscore-cell-2 {   
	background-color:$v-amber-lighten-3!important;
	.v-icon { color: $v-amber-darken-3; }
}
.k-rcram-table-simscore-cell-1 {   
	background-color:$v-grey-lighten-3!important;
	.v-icon { color: $v-grey-darken-4; font-size:12px!important; }
}
.k-rcram-table-simscore-cell-0 {   
	background-color:#fff!important;
	.v-icon { color: #666; font-size:12px!important; }
}

////////////////////////
.k-rcram-list-header {
	font-size:18px;
	line-height:23px;
	border-bottom:1px solid #000;
	padding-bottom:8px;
}

.k-cram-list-item-outer {
	border-radius:5px;
	border:1px solid #333;
	margin-bottom:8px;
}

.k-cram-list-item-header {
	padding:8px 8px 8px 0;
	// border-radius: 4px 4px 0 0;
	border-radius: 4px;
	font-size:16px;
	line-height:21px;
}

.k-cram-list-cat-icon {
	flex: 0 0 48px;
	text-align:center;
}

.k-cram-list-item-simscore {
	flex: 0 0 32px;
	text-align:left;
	font-size:12px;
	font-weight:bold;
	color:#666;
}

.k-cram-list-item-type {
	background-color:#eee;
	color:#000;
	border-radius:3px;
	padding:0 4px;
	margin-right:12px;
	white-space:nowrap;
	font-size:14px;
}

///////////////////////
.k-rcram-key {
	position:fixed;
	right:8px;
	bottom:8px;
	border:1px solid #000;
	border-radius:6px;
	background-color:#fff;
	padding:4px 0 4px 0;
	.k-rcram-table-simscore-cell-4, .k-rcram-table-simscore-cell-3, .k-rcram-table-simscore-cell-2 {   
		.v-icon { margin-top:-2px }
	}
	.k-rcram-table-simscore-cell-1, .k-rcram-table-simscore-cell-0 {   
		.v-icon { margin-top:-3px }
	}
}

.k-rcram-key-entry {
	padding:2px 8px 2px 2px;
}

.k-rcram-key-btn {
	position:fixed;
	right:8px;
	bottom:8px;
}

.k-cram-resource-description {
	width:760px;
	font-size:14px;
	line-height:18px;
}

//////////////////////
// k-rcram-table-outer
.k-rcram-report-header {
	font-size:17px;
	line-height:22px;
	padding:12px;
	max-width:800px;
	margin-left:auto;
	margin-right:auto;
	background-color:#eee;
	border-radius:12px;
}

.k-rcram-report-filters {
	display:flex;
}

.k-rcram-report-table {
	th {
		// this makes the headers sticky
		position: sticky;
		top: 0;
		z-index: 2;

		background-color:$v-indigo-darken-4;
		color:#fff!important;
		padding:8px 16px!important;
		font-size:14px!important;
		line-height:18px;
		border-left:1px solid #fff!important;
	}
	th:first-of-type {
		border-left:0!important;
	}
}

.k-rcram-comp-cell {
	font-size:16px;
	line-height:21px;
}

.k-cram-report-ancestor-comp {
	background-color:$v-indigo-lighten-5!important;
	border-top:3px solid $v-indigo-darken-3!important;
	color:#000!important;
	line-height:20px;
	padding-top:4px!important;
	padding-bottom:4px!important;
	font-size:14px!important;
	line-height:17px!important;

	p {
		text-align:center;
	}

	.k-cram-report-ancestor-comp-component {
		padding-left:8px;
	}
	.k-cram-report-ancestor-comp-component:before {
		content:"\f178";	// arrow-right-long
		font-family:'Font Awesome 6 Pro';
		margin-right:8px;
	}
}

.k-rcram-addressed-cell {
	text-align:left;
	width:164px;
	font-size:14px!important;
	white-space:nowrap;
	padding-left:8px!important;
	padding-right:8px!important;
}
.k-rcram-addressed-cell-div {
	padding:8px 8px 8px 4px;
}
.k-rcram-addressed-cell-icon {
	margin-right:4px;
}

.k-rcram-addressed-branch-cell {
	text-align:center;
	width:120px;
}

th.k-rcram-addressed-branch-cell {
	font-size:12px!important;
	line-height:14px!important;
}

.k-rcram-citations-cell {
	p {
		margin:4px 0 4px 32px!important;
		text-indent: -32px;
		font-size:14px;
		line-height:17px;
	}
	p:hover {
		text-decoration:underline;
		cursor:pointer;
	}
}

.k-rcram-citation-detail-title p {
	font-size: 17px;
	line-height:20px;
	padding-bottom:12px;
	margin-bottom:12px;
	border-bottom:1px solid #000;
}

.k-rcram-citation-detail-text {

}

.k-rcram-show-all-citations-btn {
	margin-right:-8px;
}

.k-rcram-all-citations-list {
	p {
		margin-bottom:8px;
	}
	p:hover {
		text-decoration:underline;
		cursor:pointer;
	}
}

.k-rcram-resource-branch-title {
	color:#fff;
	border-radius:4px;
	padding:4px 8px;
	margin-right:8px;
	margin-left:8px;
	p { margin:0; }
}

.k-rcram-bold-label {
	font-size:20px;
}

.k-rcram-alignment-tier-summary-element {
	white-space:nowrap;
	font-size:18px!important;
	margin-left:20px;
}

////////////////////////////////////////////////////////////
// PRINT VIEW CSS
.k-rcram-report-a-dialog-print {
	position: static!important;
    overflow: visible!important;
	box-shadow: none!important;

	a {
		color:#000!important;
	}

	.k-rcram-report-a-card {
		box-shadow:none!important;
	}

	.k-rcram-title {
		padding:0!important;
	}

	.k-rcram-report-title {
		margin-left:12px;
		font-size:16px;
	}

	.k-rcram-logo {
		position:static;
	}

	.k-rcram-print-btn {
		display:none!important;
	}

	.k-rcram-body {
		padding:12px 0 0 0!important;
	}

	.k-rcram-h1 {
		display:block;
		font-size:16px;
		.spacer { display:none; }
	}

	.k-rcram-bold-label {
		font-size:14px;
		line-height:18px;
	}

	.k-rcram-alignment-tier-summary-element {
		white-space:nowrap;
		font-size:12px!important;
	}

	.k-rcram-report-header {
		font-size:16px;
		line-height:20px;
		background-color:#fff!important;
		padding:12px 0;
		border-top:1px solid #666;
		border-bottom:1px solid #666;
		border-radius:0;
	}

	.k-rcram-report-filters {
		display:none;
	}

	.k-rcram-resource-branch-title {
		padding:0;
		margin-right:8px;
		margin-left:8px;
	}

	.k-rcram-table-outer {
		overflow: visible;
		border-radius:0;
		border:0!important;
		box-shadow:none!important;
		margin-top:24px;
		margin:24px 0;
	}

	/* Remove overflow constraints on table wrappers (essential for Vuetify v-data-table) */
	.v-data-table, .v-data-table__wrapper {
		overflow: visible !important;
		height: auto !important;
	}

	.k-rcram-report-table {
		border-radius:0!important;
		border-bottom:1px solid #000!important;
		tr {
			break-inside: avoid !important;
			page-break-inside: avoid !important;
		}

		td, th {
			break-inside: avoid !important;
			page-break-inside: avoid !important;
		}

		tr > th:last-child, tr > td:last-child {
			border-right: 1px solid #000;
		}
		tr > th:first-child, tr > td:first-child {
			border-left: 1px solid #000!important;
		}

		th {
			// no sticky headers
			position: static;
			font-size:14px!important;
			line-height:15px!important;
			padding:4px 8px!important;
			background-color:#fff!important;
			color:#000!important;
			border-top:1px solid #000!important;
			border-left:1px solid #000!important;
			border-bottom:1px solid #000!important;
		}
	}

	/* Prevent page breaks directly after rows with this class */
	tr.k-rcram-ancestor-row {
		break-after: avoid !important;
		page-break-after: avoid !important;
		break-inside: avoid !important;
		page-break-inside: avoid !important;
	}

	.k-cram-report-ancestor-comp {
		font-size:12px!important;
		line-height:15px!important;
		border-top:2px solid #000!important;
	}

	.k-rcram-comp-cell {
		font-size:13px!important;
		line-height:16px;
		padding:0 8px !important;
	}

	.k-cram-report-ancestor-comp-component {
		padding-left:6px;
	}
	.k-cram-report-ancestor-comp-component:before {
		margin-right:6px;
	}

	.k-rcram-addressed-cell {
		text-align:center;
		width:100px;
	}

	.k-rcram-addressed-cell-div {
		padding:0;
	}
	.k-rcram-addressed-cell-icon {
		margin:0;
	}
	.k-rcram-addressed-cell-label {
		display:none;
	}

	.k-rcram-addressed-branch-cell {
		width:80px;
	}

	th.k-rcram-addressed-branch-cell {
		font-size:10px!important;
		line-height:12px!important;
	}
	th.k-rcram-addressed-cell-header {
		font-size:10px!important;
		line-height:12px!important;
	}

	.k-rcram-citations-cell {
		padding:0 8px!important;
		p {
			margin:4px 0 4px 20px!important;
			text-indent: -20px;
			font-size:11px!important;
			line-height:13px!important;
		}
	}

	.k-rcram-show-all-citations-btn {
		display:none;
	}
}

.k-rcram-report-a-dialog-parent-print {
	position: static!important;
	box-shadow: none!important;
}
</style>